// src/components/SimpleFormComponents/SimpleInput.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useCallback, useEffect, useRef } from 'react';
import { Input, Form } from 'antd';
import SimpleFormItem from './SimpleFormItem';
import useFormElement from 'hooks/useFormElement';
import useCustomValidationStatus from 'hooks/useCustomValidationStatus';
import { useSimpleFormContext } from './SimpleFormProvider';
import { useModuleContext } from '../core/ModuleProvider';
import utils from 'utils/utils';
import translateX from 'utils/translateX';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const SimpleInput = ({
  name,
  subdocumentField,
  style = {},
  styleItem = {},
  disabled = false,
  prefix,
  onBlur,

  useInitialInAvatar,
  useFirstInitialInAvatar,
  useSecondInitialInAvatar,

  repeatOnField,

  validateTrigger = 'onBlur',
  showFeedback = false,

  minLength,
  maxLength,

  placeholder,

  autoFocus = false,

  ...props
}) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1) CONTEXTS
  // ─────────────────────────────────────────────────────────────────────────────
  const { locked, form, disabledFields } = useSimpleFormContext();
  const { setState, avatarInitialFirst, avatarInitialSecond } = useModuleContext();

  // ─────────────────────────────────────────────────────────────────────────────
  // 2) HOOKS
  // ─────────────────────────────────────────────────────────────────────────────
  const elementProps = useFormElement(name, subdocumentField);
  const { elementRef, fullFieldName, flattenFieldName } = elementProps;

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  // PERFORMANCE FIX: Removed Form.useWatch to prevent all SimpleInput components from re-rendering
  // when ANY field changes. Avatar is now updated in onChange handler instead.
  const isDisabled = disabled || locked || disabledFields?.includes(flattenFieldName);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  // PERFORMANCE FIX: Track last initials to avoid unnecessary re-renders
  // The calculation is cheap (string manipulation), but setState/re-render is expensive
  const lastInitialsRef = useRef(null);
  const avatarDebounceTimerRef = useRef(null);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  // PERFORMANCE FIX: Removed useEffect that was running on every form change
  // Avatar updates are now handled in onChange handler for better performance

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (avatarDebounceTimerRef.current) {
        clearTimeout(avatarDebounceTimerRef.current);
      }
    };
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4) HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const internalOnChanges = useCallback(
    (ev, ...restArgs) => {
      // PERFORMANCE FIX: Calculate initials on every keystroke (cheap operation),
      // but only call setState if initials actually changed (expensive operation)
      if (useInitialInAvatar || useFirstInitialInAvatar || useSecondInitialInAvatar) {
        const value = ev?.target?.value ?? ev ?? '';

        // Clear previous debounce timer
        if (avatarDebounceTimerRef.current) {
          clearTimeout(avatarDebounceTimerRef.current);
        }

        // Debounce to handle rapid typing (100ms)
        avatarDebounceTimerRef.current = setTimeout(() => {
          // Calculate new initials (cheap string manipulation)
          const newInitials = utils.getNameInitial(value);

          // Only update if initials actually changed (avoids expensive re-render)
          if (newInitials !== lastInitialsRef.current) {
            lastInitialsRef.current = newInitials;
            applyAvatarInitials({
              value,
              mode: useInitialInAvatar ? 'all' : useFirstInitialInAvatar ? 'first' : 'second',
              setState,
              avatarInitialFirst,
              avatarInitialSecond,
            });
          }
        }, 100);
      }

      // Pass all arguments (event, value, etc.) to external onChange handler
      if (props.onChange) props.onChange(ev, ...restArgs);
    },
    [
      useInitialInAvatar,
      useFirstInitialInAvatar,
      useSecondInitialInAvatar,
      setState,
      avatarInitialFirst,
      avatarInitialSecond,
      props.onChange,
    ]
  );

  const { customValidationStatus, customValidationMessage, handleChange } =
    useCustomValidationStatus({
      ...props,
      minLength,
      maxLength,
      onChange: internalOnChanges,
      ...elementProps,
    });

  const handleBlur = useCallback(
    ev => {
      // Defer blur-side effects to next macrotask to avoid swallowing immediate clicks.
      const value = ev?.target?.value ?? ev ?? '';

      // PERFORMANCE FIX: On blur, update avatar only if initials changed
      if (useInitialInAvatar || useFirstInitialInAvatar || useSecondInitialInAvatar) {
        // Clear debounce timer
        if (avatarDebounceTimerRef.current) {
          clearTimeout(avatarDebounceTimerRef.current);
          avatarDebounceTimerRef.current = null;
        }

        // Calculate new initials and only update if changed
        const newInitials = utils.getNameInitial(value);
        if (newInitials !== lastInitialsRef.current) {
          lastInitialsRef.current = newInitials;
          applyAvatarInitials({
            value,
            mode: useInitialInAvatar ? 'all' : useFirstInitialInAvatar ? 'first' : 'second',
            setState,
            avatarInitialFirst,
            avatarInitialSecond,
          });
        }
      }

      setTimeout(() => {
        // One-way copy on first blur if target is empty
        if (repeatOnField) {
          if (!form?.getFieldValue(repeatOnField)) {
            form?.setFieldValue(repeatOnField, value);
          }
        }

        onBlur?.(ev);
      }, 0);
    },
    [
      repeatOnField,
      form,
      onBlur,
      flattenFieldName,
      useInitialInAvatar,
      useFirstInitialInAvatar,
      useSecondInitialInAvatar,
      setState,
      avatarInitialFirst,
      avatarInitialSecond,
    ]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 5) UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <SimpleFormItem
      {...props}
      name={fullFieldName}
      elementProps={elementProps}
      customValidationStatus={customValidationStatus}
      customValidationMessage={customValidationMessage}
      style={styleItem}
      validateTrigger={validateTrigger}
      showFeedback={showFeedback}
    >
      <Input
        ref={elementRef}
        id={flattenFieldName}
        style={{ height: 40, ...style }}
        onBlur={handleBlur}
        onChange={handleChange}
        disabled={isDisabled}
        prefix={prefix}
        minLength={minLength}
        maxLength={maxLength}
        placeholder={translateX(placeholder)}
        autoFocus={autoFocus}
      />
    </SimpleFormItem>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default SimpleInput;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function applyAvatarInitials({ value, mode, setState, avatarInitialFirst, avatarInitialSecond }) {
  // No avatar update requested or setState not available (e.g., in modal without ModuleProvider)
  if (!mode || typeof setState !== 'function') return;

  const v = value ?? '';

  if (mode === 'all') {
    // Single source controls full initials
    setState({ avatarInitial: v });
    return;
  }

  if (mode === 'first') {
    setState({
      avatarInitial: `${v} ${avatarInitialSecond || ''}`.trim(),
      avatarInitialFirst: v,
    });
    return;
  }

  if (mode === 'second') {
    setState({
      avatarInitial: `${avatarInitialFirst || ''} ${v}`.trim(),
      avatarInitialSecond: v,
    });
  }
}
